import React, { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { getFp1Devices } from '../fp1.api';
import type { Fp1Device } from '../fp1.types';
import {
    clearOrderPodAssignments,
    getOrderPodAssignments,
    saveOrderPodAssignments,
    type OrderPodAssignmentsState,
    type PodAssignmentSlot,
} from '../../orders/orders.api';
import type { Order } from '../../orders/orders.types';

export interface Fp1PodSerialAssignmentModalProps {
    open: boolean;
    onClose: () => void;
    orderUuid: string;
    order: Order;
    onSaved: (order: Order) => void;
}

async function loadPodsForStatus(status: 'in_stock' | 'returned'): Promise<Fp1Device[]> {
    const acc: Fp1Device[] = [];
    let page = 1;
    const perPage = 100;
    for (;;) {
        const { data } = await getFp1Devices({
            status,
            device_type: 'fp1',
            page,
            per_page: perPage,
        });
        acc.push(...data.data);
        const lastPage = data.meta?.last_page ?? 1;
        if (page >= lastPage) {
            break;
        }
        page += 1;
    }
    return acc;
}

async function loadAvailableFp1Pods(): Promise<Fp1Device[]> {
    const [inStock, returned] = await Promise.all([
        loadPodsForStatus('in_stock'),
        loadPodsForStatus('returned'),
    ]);
    // Deduplicate by uuid (in_stock takes precedence)
    const byUuid = new Map<string, Fp1Device>();
    for (const d of inStock) {
        byUuid.set(d.uuid, d);
    }
    for (const d of returned) {
        if (!byUuid.has(d.uuid)) {
            byUuid.set(d.uuid, d);
        }
    }
    return Array.from(byUuid.values());
}

function placeholderAssignedDevice(slot: PodAssignmentSlot): Fp1Device {
    const now = new Date().toISOString();
    return {
        uuid: slot.device_uuid ?? '',
        serial_number: slot.serial_number ?? slot.device_uuid ?? '',
        device_type: 'fp1',
        model: null,
        hardware_version: null,
        firmware_version: null,
        status: 'in_stock',
        subscription_status: null,
        installation_uuid: null,
        organization_uuid: null,
        shipment_uuid: null,
        unit_cost_cents: null,
        notes: null,
        received_at: null,
        deployed_at: null,
        decommissioned_at: null,
        created_at: now,
        updated_at: now,
    };
}

/** Shape of the errors Axios/the API surface throws — every field is optional and defensively read. */
type ApiLikeError = {
    message?: string;
    code?: string;
    response?: {
        status?: number;
        statusText?: string;
        data?: {
            message?: string;
            errors?: Record<string, string[]>;
            exception?: string;
        };
        headers?: Record<string, string>;
    };
    config?: { method?: string; url?: string; baseURL?: string };
};

/**
 * Turn whatever the API threw into (1) a plain-English message for the admin and
 * (2) a copyable technical block that pinpoints the failure. The status code alone
 * tells us the category: 422 = a value we sent was rejected (the block lists exactly
 * which field and why); 5xx = the server itself errored (a backend problem, not the
 * input); no response = the server was unreachable (network/VPN/down).
 */
function buildErrorReport(e: unknown, action: string): { message: string; detail: string } {
    const err = (e ?? {}) as ApiLikeError;
    const res = err.response;
    const status = res?.status;
    const data = res?.data;

    const validationErrors = data?.errors
        ? Object.entries(data.errors).flatMap(([field, msgs]) =>
              (Array.isArray(msgs) ? msgs : [String(msgs)]).map((m) => `${field}: ${m}`),
          )
        : [];

    let message: string;
    if (!res) {
        message =
            'Could not reach the server. Check your internet connection and try again — if it keeps happening the server may be down or unreachable.';
    } else if (status === 422) {
        message =
            validationErrors[0] ??
            data?.message ??
            'One of the values entered was rejected. See the details below.';
    } else if (status && status >= 500) {
        message =
            'The server hit an unexpected error while saving. This is a problem on the server, not with what you typed. Please copy the details below and send them so it can be fixed.';
    } else if (status === 401 || status === 403) {
        message =
            'You are not authorized to make this change. Try logging out and back in; if it persists your account may lack permission.';
    } else if (status === 404) {
        message =
            'The order could not be found on the server. It may have been deleted or you may be on the wrong environment.';
    } else {
        message = data?.message ?? `The save failed (HTTP ${status ?? 'unknown'}).`;
    }

    const method = (err.config?.method ?? 'post').toUpperCase();
    // Join base + path with exactly one slash so the endpoint reads correctly
    // (axios itself inserts the slash; we mirror that here for the report).
    const base = (err.config?.baseURL ?? '').replace(/\/+$/, '');
    const path = (err.config?.url ?? '').replace(/^\/+/, '');
    const url = base || path ? `${base}/${path}` : '(unknown endpoint)';
    const headers = res?.headers ?? {};
    const requestId =
        headers['x-request-id'] ?? headers['x-amzn-requestid'] ?? headers['x-amzn-trace-id'];

    const lines: string[] = [
        `Action: ${action}`,
        `Status: ${
            status ? `${status} ${res?.statusText ?? ''}`.trim() : 'no response (network error)'
        }`,
        `Endpoint: ${method} ${url}`,
    ];
    if (requestId) {
        lines.push(`Request ID: ${requestId}`);
    }
    if (data?.message) {
        lines.push(`Server message: ${data.message}`);
    }
    if (data?.exception) {
        lines.push(`Exception: ${data.exception}`);
    }
    if (validationErrors.length) {
        lines.push('Rejected fields:');
        for (const v of validationErrors) {
            lines.push(`  • ${v}`);
        }
    }
    if (!res && err.message) {
        lines.push(`Network error: ${err.message}`);
    }
    lines.push(`When: ${new Date().toISOString()}`);

    return { message, detail: lines.join('\n') };
}

const Fp1PodSerialAssignmentModal: React.FC<Fp1PodSerialAssignmentModalProps> = ({
    open,
    onClose,
    orderUuid,
    order,
    onSaved,
}) => {
    const isReplacement = order.order_type === 'replacement';

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Copyable technical breakdown of the last failure (status, endpoint, server message). */
    const [errorDetail, setErrorDetail] = useState<string | null>(null);
    const [state, setState] = useState<OrderPodAssignmentsState | null>(null);
    const [devices, setDevices] = useState<Fp1Device[]>([]);
    const [selections, setSelections] = useState<(Fp1Device | null)[]>([]);
    /** Written to every assigned pod when the order reaches Completed / backfilled / archived (same moment devices go Deployed). */
    const [orderShipFirmware, setOrderShipFirmware] = useState('');
    /** When true, show the confirmation dialog before clearing previously-assigned serials. */
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    /** The old (defective) pod's serial, for replacement orders — optional; can be added later once the returned pod arrives. */
    const [oldSerialInput, setOldSerialInput] = useState('');
    const [oldSerialDevice, setOldSerialDevice] = useState<Fp1Device | null>(null);
    const [oldSerialLoading, setOldSerialLoading] = useState(false);
    const [oldSerialError, setOldSerialError] = useState<string | null>(null);
    /** When true, ask for confirmation before saving a replacement order without the old pod serial. */
    const [showMissingSerialsWarning, setShowMissingSerialsWarning] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setErrorDetail(null);
            try {
                const [podRes, allDevices] = await Promise.all([
                    getOrderPodAssignments(orderUuid),
                    loadAvailableFp1Pods(),
                ]);
                if (cancelled) {
                    return;
                }
                const s = podRes.data.data;
                setState(s);
                setOrderShipFirmware(s.fp1_pod_ship_firmware_version?.trim() ?? '');
                setOldSerialInput(s.old_pod?.serial_number ?? '');
                const byUuid = new Map(allDevices.map((d) => [d.uuid, d]));
                const merged = [...allDevices];
                for (const slot of s.slots) {
                    if (slot.device_uuid && !byUuid.has(slot.device_uuid)) {
                        merged.push(placeholderAssignedDevice(slot));
                        byUuid.set(slot.device_uuid, merged[merged.length - 1]);
                    }
                }
                setDevices(merged);
                setSelections(
                    s.slots.map((slot) => {
                        if (!slot.device_uuid) {
                            return null;
                        }
                        return byUuid.get(slot.device_uuid) ?? placeholderAssignedDevice(slot);
                    }),
                );
            } catch (e: unknown) {
                if (!cancelled) {
                    const report = buildErrorReport(e, 'Load pod assignment data');
                    setError(report.message);
                    setErrorDetail(report.detail);
                    // eslint-disable-next-line no-console
                    console.error('[PodSerialAssignment] failed to load assignment data', e);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, orderUuid]);

    // Live lookup for the old-pod serial — purely informational (shows current org/athlete),
    // the backend validates existence on save regardless.
    useEffect(() => {
        if (!open || !isReplacement) {
            return;
        }
        const trimmed = oldSerialInput.trim();
        if (trimmed.length < 4) {
            setOldSerialDevice(null);
            setOldSerialError(null);
            return;
        }
        const timer = setTimeout(async () => {
            setOldSerialLoading(true);
            setOldSerialError(null);
            try {
                const res = await getFp1Devices({ serial_number: trimmed, per_page: 5, page: 1 });
                const results = res.data?.data ?? [];
                if (results.length === 1) {
                    setOldSerialDevice(results[0]);
                } else {
                    setOldSerialDevice(null);
                    if (results.length === 0) {
                        setOldSerialError('No pod found with that serial number.');
                    }
                }
            } catch {
                setOldSerialDevice(null);
                setOldSerialError('Failed to look up pod.');
            } finally {
                setOldSerialLoading(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [oldSerialInput, open, isReplacement]);

    const requiredCount = state?.required_pod_count ?? 0;

    /** Slots that previously had a serial assigned but are now cleared by the user. */
    const clearedPreviouslyAssignedSlots =
        state?.slots.filter((slot, idx) => slot.device_uuid && !selections[idx]) ?? [];

    /** True if every slot is now empty (user cleared everything). */
    const allSlotsEmpty = selections.every((s) => !s);

    /** True if at least one slot is filled and at least one is empty — an unsupported partial state. */
    const mixedEmptyFilled =
        !allSlotsEmpty && selections.some((s) => !s) && selections.some((s) => !!s);

    const handleConfirm = async () => {
        if (!state) {
            return;
        }

        // Mixed state: some slots filled, some empty — ask user to resolve.
        if (mixedEmptyFilled) {
            setError(
                'Please assign a serial number to every pod slot, or clear all slots to remove all assignments.',
            );
            setErrorDetail(null);
            return;
        }

        // All slots cleared — check if any previously had assignments to show confirmation.
        if (allSlotsEmpty) {
            if (clearedPreviouslyAssignedSlots.length > 0) {
                // Ask for confirmation before removing previously saved serials.
                setShowClearConfirm(true);
                return;
            }
            // Nothing was ever assigned — just close.
            onClose();
            return;
        }

        // The old pod serial is optional on replacement orders (the user may not know
        // which pod is defective until it arrives back), but confirm before saving
        // without it so it isn't skipped by accident.
        if (isReplacement && oldSerialInput.trim() === '') {
            setShowMissingSerialsWarning(true);
            return;
        }

        // All slots filled — normal save path.
        await doSave();
    };

    const doSave = async () => {
        setSubmitting(true);
        setError(null);
        setErrorDetail(null);
        try {
            const fw = orderShipFirmware.trim();
            const res = await saveOrderPodAssignments(
                orderUuid,
                selections.map((d) => d!.uuid),
                {
                    firmware_version: fw === '' ? null : fw,
                    ...(isReplacement ? { old_pod_serial_number: oldSerialInput.trim() } : {}),
                },
            );
            onSaved(res.data.data.order);
            onClose();
        } catch (e: unknown) {
            const report = buildErrorReport(
                e,
                isReplacement
                    ? 'Save pod serials (replacement — new + old pod)'
                    : 'Save pod serials',
            );
            setError(report.message);
            setErrorDetail(report.detail);
            // eslint-disable-next-line no-console
            console.error('[PodSerialAssignment] save failed', e);
        } finally {
            setSubmitting(false);
        }
    };

    const doConfirmedClear = async () => {
        setShowClearConfirm(false);
        setSubmitting(true);
        setError(null);
        setErrorDetail(null);
        try {
            const res = await clearOrderPodAssignments(orderUuid);
            onSaved(res.data.data.order);
            onClose();
        } catch (e: unknown) {
            const report = buildErrorReport(e, 'Remove pod serial assignments');
            setError(report.message);
            setErrorDetail(report.detail);
            // eslint-disable-next-line no-console
            console.error('[PodSerialAssignment] clear failed', e);
        } finally {
            setSubmitting(false);
        }
    };

    const clearedSerialLabels = clearedPreviouslyAssignedSlots
        .map((slot) => slot.serial_number ?? slot.device_uuid ?? `Pod #${slot.slot_index ?? 0}`)
        .join(', ');

    const errorAlert = error ? (
        <Alert severity="error" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
            <Typography variant="body2">{error}</Typography>
            {errorDetail && (
                <>
                    <Box
                        component="pre"
                        sx={{
                            mt: 1,
                            mb: 0,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'rgba(0, 0, 0, 0.06)',
                            fontFamily: 'monospace',
                            fontSize: 11,
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: 200,
                            overflow: 'auto',
                        }}
                    >
                        {errorDetail}
                    </Box>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        sx={{ mt: 1 }}
                        onClick={() => {
                            const text = errorDetail;
                            if (navigator.clipboard?.writeText) {
                                void navigator.clipboard.writeText(text);
                            }
                        }}
                    >
                        Copy error details
                    </Button>
                </>
            )}
        </Alert>
    ) : null;

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Assign Pod Serial Numbers — {requiredCount} pod{requiredCount === 1 ? '' : 's'}{' '}
                    in this order
                </DialogTitle>
                <DialogContent>
                    {loading && (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress size={32} />
                        </Box>
                    )}
                    {!loading && !state && (
                        // Loading the assignment data itself failed — show the error (with detail)
                        // instead of an empty dialog so the problem is visible.
                        <Box sx={{ pt: 1 }}>
                            {errorAlert ?? (
                                <Typography variant="body2" color="text.secondary">
                                    No pod assignment data available for this order.
                                </Typography>
                            )}
                        </Box>
                    )}
                    {!loading && state && (
                        <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
                            {errorAlert}
                            <TextField
                                label="Firmware version for this order (all pods)"
                                value={orderShipFirmware}
                                onChange={(e) => setOrderShipFirmware(e.target.value)}
                                size="small"
                                fullWidth
                                placeholder="e.g. 1.2.3"
                                helperText="One value for every pod on this order. It is stored on the order and saved to each device when the order reaches Completed (or equivalent fulfillment), when pods become Deployed."
                            />
                            {state.slots.map((slot, idx) => {
                                const selectedElsewhere = new Set(
                                    selections
                                        .map((s, j) => (j !== idx && s ? s.uuid : null))
                                        .filter(Boolean) as string[],
                                );
                                const unavailable = new Set(state.unavailable_device_uuids ?? []);
                                return (
                                    <Autocomplete
                                        key={slot.slot_index}
                                        options={devices}
                                        value={selections[idx] ?? null}
                                        onChange={(_, v) => {
                                            setError(null);
                                            setErrorDetail(null);
                                            setSelections((prev) => {
                                                const next = [...prev];
                                                next[idx] = v;
                                                return next;
                                            });
                                        }}
                                        getOptionLabel={(d) => d.serial_number}
                                        isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
                                        getOptionDisabled={(d) => {
                                            if (unavailable.has(d.uuid)) {
                                                return true;
                                            }
                                            if (selectedElsewhere.has(d.uuid)) {
                                                return true;
                                            }
                                            return false;
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={`Pod #${idx + 1}`}
                                                size="small"
                                                helperText={
                                                    slot.device_uuid && !selections[idx]
                                                        ? `Previously: ${
                                                              slot.serial_number ?? slot.device_uuid
                                                          } — saving will remove this assignment`
                                                        : undefined
                                                }
                                                FormHelperTextProps={{
                                                    style: { color: '#ed6c02' },
                                                }}
                                            />
                                        )}
                                    />
                                );
                            })}
                            {isReplacement && (
                                <Box>
                                    <TextField
                                        label="Old Pod Serial Number"
                                        value={oldSerialInput}
                                        onChange={(e) => {
                                            setError(null);
                                            setErrorDetail(null);
                                            setOldSerialInput(e.target.value);
                                        }}
                                        size="small"
                                        fullWidth
                                        placeholder="e.g. FP1-XXXXXXXX"
                                        helperText={
                                            oldSerialError ??
                                            'The defective pod being returned. Optional — if you don’t know it yet, you can add it here later (e.g. once the pod arrives back). When recorded, it is detached from its org/athlete and marked Returning after the order ships.'
                                        }
                                        error={!!oldSerialError}
                                        InputProps={{
                                            endAdornment: oldSerialLoading ? (
                                                <CircularProgress size={16} />
                                            ) : undefined,
                                            sx: { fontFamily: 'monospace' },
                                        }}
                                    />
                                    {oldSerialDevice && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mt: 0.5, display: 'block' }}
                                        >
                                            Currently: {oldSerialDevice.status ?? '—'} ·{' '}
                                            {oldSerialDevice.assigned_org?.name ??
                                                oldSerialDevice.organization_name ??
                                                'No org'}
                                            {oldSerialDevice.assigned_athlete
                                                ? ` · ${
                                                      oldSerialDevice.assigned_athlete.name ??
                                                      oldSerialDevice.assigned_athlete.email
                                                  }`
                                                : ''}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={submitting || loading}
                        color={
                            clearedPreviouslyAssignedSlots.length > 0 && allSlotsEmpty
                                ? 'error'
                                : 'primary'
                        }
                    >
                        {submitting ? (
                            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                        ) : null}
                        {clearedPreviouslyAssignedSlots.length > 0 && allSlotsEmpty
                            ? 'Remove Assignments'
                            : 'Confirm Assignments'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation dialog shown when user tries to save with previously-assigned slots cleared */}
            <Dialog
                open={showClearConfirm}
                onClose={() => setShowClearConfirm(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Remove Serial Assignments?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        You are about to remove the following serial assignment
                        {clearedPreviouslyAssignedSlots.length > 1 ? 's' : ''} from this order:
                    </Alert>
                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
                        {clearedSerialLabels}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        The device{clearedPreviouslyAssignedSlots.length > 1 ? 's' : ''} will be
                        unlinked from this order. This cannot be undone without re-assigning.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowClearConfirm(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={doConfirmedClear}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                        ) : null}
                        Yes, Remove
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Soft confirmation — replacement order being saved without the old pod serial */}
            <Dialog
                open={showMissingSerialsWarning}
                onClose={() => setShowMissingSerialsWarning(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>No Old Pod Serial Number</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                        You haven&apos;t entered the old (defective) pod&apos;s serial number.
                    </Alert>
                    <Typography variant="body2">
                        Until it is recorded, the old pod stays attached to its athlete/org and
                        won&apos;t be detached when the new pod ships. If you don&apos;t know which
                        pod is defective yet, you can continue now and add the serial to this order
                        later — for example, once the pod arrives back at the office.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowMissingSerialsWarning(false);
                            void doSave();
                        }}
                        disabled={submitting}
                    >
                        Continue Without It
                    </Button>
                    <Button variant="contained" onClick={() => setShowMissingSerialsWarning(false)}>
                        Go Back
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Fp1PodSerialAssignmentModal;
